import type {
  DraftAdditionalDataContent,
  DraftContent,
  DraftLineContent,
  MarginProcedure,
  PaymentMethod,
  VatRate,
} from './api';
import { emptyDraftContent } from './api';

/**
 * Editor-facing form state. Everything is a plain `string` (empty = "not filled") so inputs stay
 * controlled; `contentFromEditorState` converts blanks back to the `null`s the API expects. Kept
 * separate from `DraftContent` so the form never has to reason about `null` vs `''`.
 */
export interface LineDraft {
  key: number;
  name: string;
  unitOfMeasure: string;
  quantity: string;
  unitNetPrice: string;
  /** Gross unit price — used instead of `unitNetPrice` on a margin draft. */
  unitGrossPrice: string;
  vatRate: VatRate | '';
}

export type SaleMode = 'day' | 'period';

/**
 * One additional-data (`DodatkowyOpis`) row in the editor. `id` is a stable React key (like
 * `LineDraft.key`); `key`/`value` are the `Klucz`/`Wartosc` strings (empty = "not filled").
 */
export interface AdditionalDataDraft {
  id: number;
  key: string;
  value: string;
}

export interface EditorState {
  numberOverride: string;
  saleMode: SaleMode;
  saleOnDate: string;
  salePeriodFrom: string;
  salePeriodTo: string;
  lines: LineDraft[];
  paymentMethod: PaymentMethod | '';
  paymentDueDate: string;
  paymentPaidDate: string;
  paymentBankAccount: string;
  exemptionLegalBasis: string;
  additionalData: AdditionalDataDraft[];
  footerNote: string;
  /** The chosen margin procedure; empty until picked. Only meaningful on a margin draft. */
  marginProcedure: MarginProcedure | '';
}

export function emptyLineDraft(key: number): LineDraft {
  return {
    key,
    name: '',
    unitOfMeasure: '',
    quantity: '',
    unitNetPrice: '',
    unitGrossPrice: '',
    vatRate: '',
  };
}

export function emptyAdditionalDataDraft(id: number): AdditionalDataDraft {
  return { id, key: '', value: '' };
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** A typed decimal → the canonical form the API expects (Polish comma → dot); blank becomes null. */
function decimalToNull(value: string): string | null {
  const normalized = value.trim().replace(',', '.');
  return normalized === '' ? null : normalized;
}

/**
 * Keeps only what a decimal amount may hold as the user types: digits and a single separator (comma
 * or dot). Strips letters/spaces so numeric fields can't accept junk; the separator is localized to a
 * dot on submit (see `decimalToNull`).
 */
export function sanitizeDecimal(value: string): string {
  const cleaned = value.replace(/[^\d.,]/g, '');
  const sep = cleaned.search(/[.,]/);
  if (sep === -1) return cleaned;
  return cleaned.slice(0, sep + 1) + cleaned.slice(sep + 1).replace(/[.,]/g, '');
}

/** Seeds editor state from a persisted `DraftContent`. `nextKey` mints stable React keys for lines. */
export function editorStateFromContent(content: DraftContent, nextKey: () => number): EditorState {
  const saleDate = content.saleDate;
  const isPeriod = saleDate != null && (saleDate.periodFrom != null || saleDate.periodTo != null);
  const lines: LineDraft[] =
    content.lines.length > 0
      ? content.lines.map((l) => ({
          key: nextKey(),
          name: l.name ?? '',
          unitOfMeasure: l.unitOfMeasure ?? '',
          quantity: l.quantity ?? '',
          unitNetPrice: l.unitNetPrice ?? '',
          unitGrossPrice: l.unitGrossPrice ?? '',
          vatRate: (l.vatRate as VatRate | null) ?? '',
        }))
      : [emptyLineDraft(nextKey())];

  return {
    numberOverride: content.numberOverride ?? '',
    saleMode: isPeriod ? 'period' : 'day',
    saleOnDate: saleDate?.onDate ?? '',
    salePeriodFrom: saleDate?.periodFrom ?? '',
    salePeriodTo: saleDate?.periodTo ?? '',
    lines,
    paymentMethod: (content.payment.method as PaymentMethod | null) ?? '',
    paymentDueDate: content.payment.dueDate ?? '',
    paymentPaidDate: content.payment.paidDate ?? '',
    paymentBankAccount: content.payment.bankAccount ?? '',
    exemptionLegalBasis: content.exemptionLegalBasis ?? '',
    additionalData: content.additionalData.map((entry) => ({
      id: nextKey(),
      key: entry.key ?? '',
      value: entry.value ?? '',
    })),
    footerNote: content.footerNote ?? '',
    marginProcedure: (content.marginProcedure as MarginProcedure | null) ?? '',
  };
}

/** A fresh editor state (one blank line), for a draft with no content yet. */
export function emptyEditorState(nextKey: () => number): EditorState {
  return editorStateFromContent(emptyDraftContent(), nextKey);
}

/** Converts editor state back to the lenient `DraftContent` sent on validate/autosave. */
export function contentFromEditorState(state: EditorState): DraftContent {
  const saleDate =
    state.saleMode === 'period'
      ? state.salePeriodFrom.trim() !== '' || state.salePeriodTo.trim() !== ''
        ? {
            onDate: null,
            periodFrom: blankToNull(state.salePeriodFrom),
            periodTo: blankToNull(state.salePeriodTo),
          }
        : null
      : state.saleOnDate.trim() !== ''
        ? { onDate: state.saleOnDate.trim(), periodFrom: null, periodTo: null }
        : null;

  return {
    currencyCode: null,
    numberOverride: blankToNull(state.numberOverride),
    saleDate,
    lines: state.lines.map((l) => ({
      name: blankToNull(l.name),
      unitOfMeasure: blankToNull(l.unitOfMeasure),
      quantity: decimalToNull(l.quantity),
      unitNetPrice: decimalToNull(l.unitNetPrice),
      unitGrossPrice: decimalToNull(l.unitGrossPrice),
      vatRate: l.vatRate === '' ? null : l.vatRate,
    })),
    payment: {
      method: state.paymentMethod === '' ? null : state.paymentMethod,
      dueDate: blankToNull(state.paymentDueDate),
      paidDate: blankToNull(state.paymentPaidDate),
      bankAccount: blankToNull(state.paymentBankAccount),
    },
    exemptionLegalBasis: blankToNull(state.exemptionLegalBasis),
    additionalData: additionalDataFromEditorState(state.additionalData),
    footerNote: blankToNull(state.footerNote),
    marginProcedure: state.marginProcedure === '' ? null : state.marginProcedure,
  };
}

/**
 * Converts editor additional-data rows to the lenient wire shape, keeping every row (including
 * fully-blank ones) so a row's position matches the 1-based index the evaluator reports errors against
 * — the same all-rows-sent contract the lines use. Blank rows carry no intent: the evaluator drops
 * them silently, and `isDraftContentBlank` treats an all-blank list as empty so an untyped row never
 * trips lazy-create.
 */
function additionalDataFromEditorState(rows: AdditionalDataDraft[]): DraftAdditionalDataContent[] {
  return rows.map((row) => ({ key: blankToNull(row.key), value: blankToNull(row.value) }));
}

/** Whether any line uses the EXEMPT rate — the composer reveals the exemption-basis field when so. */
export function hasExemptLine(state: EditorState): boolean {
  return state.lines.some((l) => l.vatRate === 'EXEMPT');
}

function isLineBlank(line: DraftLineContent): boolean {
  return (
    line.name == null &&
    line.unitOfMeasure == null &&
    line.quantity == null &&
    line.unitNetPrice == null &&
    line.unitGrossPrice == null &&
    line.vatRate == null
  );
}

/**
 * Whether a draft body carries no meaningful content yet — every line blank, no sale date, number
 * override, payment detail, or exemption basis. Drives lazy-create: while the composer is still on this
 * (plus no linked buyer) it holds off creating a server row, so `/invoices/new/standard` never leaves an
 * abandoned empty draft behind. The linked buyer is tracked separately (it isn't part of `DraftContent`),
 * so a customer-first entry counts as meaningful even against a blank body.
 */
export function isDraftContentBlank(content: DraftContent): boolean {
  return (
    content.currencyCode == null &&
    content.numberOverride == null &&
    content.saleDate == null &&
    content.exemptionLegalBasis == null &&
    content.payment.method == null &&
    content.payment.dueDate == null &&
    content.payment.paidDate == null &&
    content.payment.bankAccount == null &&
    content.footerNote == null &&
    content.marginProcedure == null &&
    content.additionalData.every((row) => row.key == null && row.value == null) &&
    content.lines.every(isLineBlank)
  );
}
